// @flow

/**
 * MultiColumnLayoutEngine is a virtual base class intended to be used for any
 * layouts that use multiple columns to lay out items. It supports min number of
 * cols, fixed or variable-width cols, and gutter sizes.
 */

import Layout, { type Size } from 'app/common/react/components/Masonry9/Layout';
import {
  type LayoutEngine,
  type buildLayoutProps,
} from 'app/common/react/components/Masonry9/LayoutEngine';

const DEFAULT_MIN_COLS = 2;
const DEFAULT_COLUMN_WIDTH = 250;
const DEFAULT_GUTTER_SIZE = 20;
const DEFAULT_FLEXIBLE = false;

type Props = {|
  minCols?: number,
  colWidth?: number,
  gutter?: number,
  flexible?: boolean,
|};

export default class MultiColumnLayoutEngine<ItemType> implements LayoutEngine<ItemType> {
  minCols: number;

  colWidth: number;

  gutter: number;

  flexible: boolean;

  constructor(props?: Props) {
    this.minCols = props && typeof props.minCols === 'number' ? props.minCols : DEFAULT_MIN_COLS;
    this.colWidth =
      props && typeof props.colWidth === 'number' ? props.colWidth : DEFAULT_COLUMN_WIDTH;
    this.gutter = props && typeof props.gutter === 'number' ? props.gutter : DEFAULT_GUTTER_SIZE;
    this.flexible =
      props && typeof props.flexible === 'boolean' ? props.flexible : DEFAULT_FLEXIBLE;
  }

  // Based on an array of column heights, determine where we should place a new
  // item.
  static getColumnIndex(columnHeights: Array<number>) {
    let curMinIndex = 0;

    for (let i = 0; i < columnHeights.length; i += 1) {
      if (columnHeights[i] < columnHeights[curMinIndex]) {
        curMinIndex = i;
      }
    }

    return curMinIndex;
  }

  // Based on the layout config and available space, determine how many columns
  // are available.
  getColumnInfo(containerSize: ?Size) {
    const { gutter, minCols } = this;

    let { colWidth: width } = this;
    let count: number;

    // Bail early if the width is 0.
    if (!containerSize || width === 0) {
      return { width: 0, count: 0 };
    }

    if (this.flexible) {
      const colGuess = Math.floor(containerSize.width / width);
      count = Math.max(Math.floor((containerSize.width - colGuess * gutter) / width), minCols);

      if (containerSize.width) {
        width = Math.floor((containerSize.width - (count + 1) * gutter) / count);
      }
    } else {
      const fluidColumnCount = Math.floor((containerSize.width + gutter) / (width + gutter));
      count = Math.max(fluidColumnCount, minCols);
    }

    return { width, count };
  }

  buildLayout(props: buildLayoutProps<ItemType>): Layout<ItemType> {
    throw new Error('Subclasses must implement');
  }
}
